/**
 * Flow Queuing System — the enroll-queue provider's item-level autosave
 * machinery, extracted verbatim from `EnrollQueueContext.tsx` (terra-ohe.7,
 * F1/DBOS durable-send migration U6: "the autosave machinery would live in
 * its own `useItemAutosave.ts` beside `useSendLock.ts` and
 * `useQueueUiSignals.ts` rather than inside the 1,995-line context" — the
 * in-source reservation this bead closes).
 *
 * `patchMutation` (the PATCH `/api/flow/queue/items/:id` mutation object)
 * STAYS declared in the context file — its `onSuccess` wires
 * `clearSendSkipLabel` + `QUEUE_KEY` invalidation, which this hook has no
 * business touching — and is handed to this hook by reference, structurally
 * typed to just the `mutateAsync` shape this file calls (the same pattern
 * `useQueueUiSignals.ts` already uses for `removeMutation`/`unstickMutation`/
 * `sendMutation`).
 *
 * Design decision (Deferred to Implementation, per the bead): the
 * write-barrier refs (`isItemWriteBarred`) and the destructive-op queue
 * (`awaitItemOps`) stay declared in the context file and are handed in by
 * reference — moving them in would pull `useItemOpQueue`'s wiring and the
 * reassignment/reset write-barrier claims along with them for no reduction
 * in the context file's own complexity. Smaller diff wins.
 *
 * Deviation from the bead's literal `useItemAutosave({ patchMutation,
 * isItemWriteBarred, awaitItemOps, queryClient })` args list: `queryClient`
 * is dropped. Nothing in the moved bodies (`patchItem`/`dispatchAutosave`/
 * `autosavePatch`/`discardAutosave`/`flushAutosave`) ever read it —
 * `patchMutation`'s own `onSuccess` already owns every cache invalidation
 * this machinery needs — so threading it through would be an unused
 * parameter. Noted in the implementation report rather than silently
 * dropped, per the bead's instruction on spec/plan conflicts.
 *
 * The compensating-rollback plumbing (`TrackedItemPatch.rollback`,
 * `autosavePatch`'s internal 4th argument, `suppressSkipLabelClear`) is
 * DELETED here, not carried over: it existed for exactly one caller — the
 * deleted client-side materialization function's own rollback closure (see
 * `state/use-staged-materialization.ts` and the coding standards' "no
 * rollback helper" rule). `patchMutation.onSuccess` now clears the
 * server-authoritative skip label on every successful patch unconditionally,
 * matching the D9 generalized-seam rule with no exception left to carve out.
 */
import { useCallback, useRef, type MutableRefObject } from 'react';
import type { EnrollmentItem } from '@shared/flow/enrollment';
import type {
  EnrollQueueContextValue,
  ItemAutosaveOwnership,
  ItemAutosavePatch,
} from './enroll-queue-types';

/**
 * U2 (buttonless autosaving editor) — trailing-edge debounce for the item
 * editor's autosave PATCH (replaces the removed "Save draft" button's
 * on-click fire). Item-level ONLY: Gong step overrides are local-until-Send
 * (plan 2026-08-19-004) and never debounce.
 */
export const AUTOSAVE_DEBOUNCE_MS = 800;

/**
 * Round-3 (F1/F2, plan 2026-08-19-003) — one debounced-write entry for the
 * ITEM-level autosave map. The round-2 shape deleted an entry the instant
 * its write was DISPATCHED, which made in-flight and failed writes invisible
 * to the flush gate (F1) and let an edit-during-flight start an unordered
 * sibling write (F2). The new invariant: **an entry exists iff something is
 * unsaved, in flight, or failed** — deletion is acknowledgement-gated.
 *
 * Lifecycle:
 * - An edit merges into `pendingPatch` and (re)starts the debounce `timer`,
 *   whether or not a write is in flight.
 * - Timer fire is a *dispatch attempt*, not a deletion: if nothing is in
 *   flight, `pendingPatch` moves into a write whose settle-chained promise
 *   is stored on `inFlight`; if a write IS in flight, nothing happens — the
 *   settle handler dispatches the queued patch next. Never two concurrent
 *   writes per key ⇒ writes commit in dispatch order ⇒ F2's out-of-order
 *   revert is structurally impossible from one tab (cross-tab stays a
 *   deferred server-versioning concern).
 * - On success: clear `lastError`; delete the entry only when fully drained
 *   (no `pendingPatch`, no `timer`), else dispatch the queued patch.
 * - On failure: fold the dispatched patch back into `pendingPatch` (newer
 *   field edits win), retain the error on `lastError`, do NOT auto-retry —
 *   the next edit's dispatch or a flush retries.
 *
 * `inFlight` always holds the settle-CHAINED promise (its `.then`/`.catch`
 * bookkeeping already attached), so it never rejects — failures are read
 * back off `lastError`. That is what lets a flush `await` any combination of
 * in-flight writes with all-settled semantics for free.
 */
export type DebouncedWriteEntry<Patch> = {
  timer: ReturnType<typeof setTimeout> | null;
  pendingPatch: Patch | null;
  inFlight: Promise<void> | null;
  /**
   * The patch `inFlight` is currently writing, retained for the duration of
   * that write and cleared when it settles (design-fidelity plan D8 Part B
   * point 4). Only the deleted client-side materialization function ever
   * read it — `state/staged-play-config.ts`'s `buildStagedSendSnapshot`, its
   * wire-shape replacement, does no resolution and never reads it, since the
   * server resolves precedence itself since U2. Retained for the ghost-sweep
   * effect's release condition in `EnrollQueueContext.tsx`, which still
   * reads the entry's shape generically.
   */
  inFlightPatch: Patch | null;
  /**
   * The last patch this item's PATCH ACKNOWLEDGED, retained until a queue
   * read actually reflects it (D8 regression #2) — `patchMutation.onSuccess`
   * invalidates `QUEUE_KEY` FIRE-AND-FORGET (deliberately — the per-keystroke
   * autosave loop needs a fast resolution), so between the ack and the
   * refetch landing there is a real window in which the entry is gone AND
   * `groups` still shows the item's pre-patch values. The provider's
   * ghost-sweep effect releases this once the item in `groups` carries these
   * values (or the item leaves the queue).
   */
  ackedPatch: Patch | null;
  lastError: unknown;
};

/**
 * "Does the server-known item now carry this patch?" — the release condition
 * for `ackedPatch` above, and for the draft-reset generation settle pass in
 * `EnrollQueueContext.tsx`.
 *
 * Draft fields are compared through the route's OWN normalization: `PATCH
 * /api/flow/queue/items/:id` stores a blank draft as NULL (`blankToNull`,
 * src/services/flow/queue.ts), so a patch carrying `'   '` reads back as
 * `null`, and a naive comparison would never match — leaving the entry
 * retained (and this item pinned to that patch) for the rest of the session.
 * `sender` is stored verbatim by that same route, so it is compared raw apart
 * from the `?? ''` the patch shape already implies.
 *
 * D12 Codex fix (#1–#3): patches are SPARSE, so only the halves the patch
 * actually carries are compared — an absent field was never written and
 * therefore has nothing to reflect.
 */
export function itemReflectsPatch(item: EnrollmentItem, patch: ItemAutosavePatch): boolean {
  const blankToNull = (value: string): string => (value.trim() === '' ? '' : value);
  if (patch.sender !== undefined && (item.sender ?? '') !== patch.sender) return false;
  if (patch.subject_draft !== undefined && (item.subject_draft ?? '') !== blankToNull(patch.subject_draft)) return false;
  if (patch.body_draft !== undefined && (item.body_draft ?? '') !== blankToNull(patch.body_draft)) return false;
  return true;
}

/**
 * Round-3 — normalizes a retained `lastError` (always an `Error` in practice:
 * every mutationFn rejects with one) for a flush's rethrow without tripping
 * `no-base-to-string` on a defensive non-Error value.
 */
export function asFlushError(err: unknown): Error {
  if (err instanceof Error) return err;
  return new Error(typeof err === 'string' ? err : 'Failed to save your latest edits');
}

/**
 * D12 Codex fix, findings #1–#3 — one tracked autosave write: the sparse
 * PATCH body plus per-half provenance. Everything the entry machinery holds
 * (`pendingPatch`/`inFlightPatch`/`ackedPatch`) is one of these, so "is this
 * value the rep's own choice?" travels with the value instead of being
 * re-guessed by each reader.
 */
export interface TrackedItemPatch {
  patch: ItemAutosavePatch;
  owned: ItemAutosaveOwnership;
}

export interface UseItemAutosaveDeps {
  /**
   * `EnrollQueueContext.tsx`'s `patchMutation` — structurally typed to just
   * the `mutateAsync` shape this file calls, matching `useQueueUiSignals.ts`'s
   * precedent for a mutation object it does not own.
   */
  patchMutation: { mutateAsync: (input: { itemId: number; patch: Record<string, unknown> }) => Promise<unknown> };
  /**
   * D12 Codex round-2 fixes #3/#4 — the per-item autosave write barrier
   * (`EnrollQueueContext.tsx`'s `writeBarriersRef`). A reset or reassignment
   * in progress means every value for this item is about to be replaced by
   * the server's; both the scheduling half (`autosavePatch`) and the dispatch
   * half (`dispatchAutosave`) refuse for the duration.
   */
  isItemWriteBarred: (itemId: number) => boolean;
  /**
   * D12 Codex round-3 fix #1 — `flushAutosave` must wait out any queued
   * destructive op (`useItemOpQueue.awaitItemOps`) before trusting
   * `dispatchAutosave`'s refusal-while-barred as "genuinely drained" rather
   * than "silently withheld".
   */
  awaitItemOps: (itemId: number) => Promise<void>;
  /**
   * The trailing-edge debounce delay, in ms. Defaults to the production
   * `AUTOSAVE_DEBOUNCE_MS` when omitted, which is what every non-test caller
   * gets — `EnrollQueueProvider` only passes something else when its own
   * `timers` prop is set (see `EnrollQueueTimers`). Injected rather than read
   * from the module constant so the queue's real-composition test suites can
   * exercise this exact code path without paying an 800ms wall-clock wait per
   * edit.
   */
  autosaveDebounceMs?: number;
}

export interface ItemAutosave {
  patchItem: EnrollQueueContextValue['patchItem'];
  autosavePatch: EnrollQueueContextValue['autosavePatch'];
  /** The debounce timer's dispatch attempt — exposed for the settle-handoff chain; not part of the public context value. */
  dispatchAutosave: (itemId: number) => void;
  flushAutosave: EnrollQueueContextValue['flushAutosave'];
  discardAutosave: EnrollQueueContextValue['discardAutosave'];
  /**
   * The raw entry map, returned by reference so the provider's ghost-sweep
   * effect (releasing `ackedPatch` once a queue read reflects it) and its
   * unmount-cleanup effect (clearing pending timers) keep working unchanged
   * against the SAME map this hook writes.
   */
  autosaveEntriesRef: MutableRefObject<Map<number, DebouncedWriteEntry<TrackedItemPatch>>>;
}

export function useItemAutosave({
  patchMutation,
  isItemWriteBarred,
  awaitItemOps,
  autosaveDebounceMs = AUTOSAVE_DEBOUNCE_MS,
}: UseItemAutosaveDeps): ItemAutosave {
  /**
   * U2 (buttonless autosaving editor) — one acknowledgement-gated
   * `DebouncedWriteEntry` PER ITEM id (see the type's doc comment for the
   * round-3 lifecycle).
   */
  const autosaveEntriesRef = useRef<Map<number, DebouncedWriteEntry<TrackedItemPatch>>>(new Map());

  const patchItem = useCallback(
    async (itemId: number, patch: Record<string, unknown>) => {
      await patchMutation.mutateAsync({ itemId, patch });
    },
    [patchMutation],
  );

  const { mutateAsync: patchItemAsync } = patchMutation;

  /**
   * Round-3 (F1/F2) — the item-level *dispatch attempt* (see
   * `DebouncedWriteEntry`'s lifecycle doc). No-ops when a PATCH is already
   * in flight for this item (the settle handler dispatches the queued patch
   * next — max one concurrent write per key) or when nothing is pending.
   * Called by the debounce timer, by the settle handler (queued-patch
   * handoff), and by `flushAutosave`.
   */
  const dispatchAutosave = useCallback((itemId: number) => {
    const run = (id: number) => {
      // D12 Codex round-2 fixes #3/#4 — the write barrier's dispatch half.
      if (isItemWriteBarred(id)) return;
      const entry = autosaveEntriesRef.current.get(id);
      if (!entry || entry.inFlight !== null || entry.pendingPatch === null) return;
      const dispatched = entry.pendingPatch;
      entry.pendingPatch = null;
      entry.inFlightPatch = dispatched;
      entry.inFlight = patchItemAsync({ itemId: id, patch: dispatched.patch })
        .then(() => {
          entry.inFlight = null;
          entry.inFlightPatch = null;
          entry.lastError = null;
          // D8 regression #2 — the ack no longer DELETES the entry when it
          // drains: it hands the dispatched patch to `ackedPatch`, which the
          // queue-driven reconcile pass (in `EnrollQueueContext.tsx`) releases
          // once a read actually reflects it.
          entry.ackedPatch = dispatched;
          if (entry.pendingPatch !== null && entry.timer === null) {
            run(id);
          }
          // A ticking timer means a fresher edit is still coalescing — its
          // fire (or a flush) dispatches it.
        })
        .catch((err: unknown) => {
          entry.inFlight = null;
          entry.inFlightPatch = null;
          entry.lastError = err;
          // Replace-merge (each autosavePatch call carries the FULL patch):
          // a newer pending patch supersedes the failed one entirely; only
          // fold the dispatched patch back when nothing newer queued up.
          entry.pendingPatch = entry.pendingPatch ?? dispatched;
        });
    };
    run(itemId);
  }, [patchItemAsync, isItemWriteBarred]);

  /**
   * Schedules a trailing-edge debounced draft-field PATCH for `itemId` —
   * replaces the removed "Save draft" button's on-click PATCH. Fire-and-
   * forget by design (the editor calls this from an effect on every keystroke
   * settle, never awaits it): a later call for the SAME item before the timer
   * fires replaces the pending patch (each call carries the full field set),
   * so rapid typing coalesces into exactly one network PATCH per pause,
   * never one per keystroke. Round-3: the entry persists through dispatch —
   * an edit landing while a PATCH is in flight queues on the same entry and
   * dispatches after it settles (never an unordered sibling PATCH — F2).
   */
  const autosavePatch = useCallback(
    (itemId: number, patch: ItemAutosavePatch, owned: ItemAutosaveOwnership) => {
      // D12 Codex round-2 fixes #3/#4 — the write barrier's SCHEDULING half.
      if (isItemWriteBarred(itemId)) return;
      let entry = autosaveEntriesRef.current.get(itemId);
      if (!entry) {
        entry = { timer: null, pendingPatch: null, inFlight: null, inFlightPatch: null, ackedPatch: null, lastError: null };
        autosaveEntriesRef.current.set(itemId, entry);
      }
      // Replace, never per-field merge — see `autosavePatch`'s contract in
      // enroll-queue-types.ts: every caller enqueues CUMULATIVELY.
      entry.pendingPatch = { patch, owned };
      if (entry.timer !== null) clearTimeout(entry.timer);
      const stable = entry;
      entry.timer = setTimeout(() => {
        stable.timer = null;
        dispatchAutosave(itemId);
      }, autosaveDebounceMs);
    },
    // `autosaveDebounceMs` is a dep, not a ref read: without it this callback
    // would keep the FIRST render's delay forever. It is a plain number, so a
    // caller re-creating its `timers` object literal every render does not
    // churn this callback's identity (and therefore not the context value's).
    [dispatchAutosave, isItemWriteBarred, autosaveDebounceMs],
  );

  /**
   * See `EnrollQueueContextValue.discardAutosave` for the contract and the
   * race it closes. The inverse of `flushAutosave`: that one COMMITS what is
   * queued, this one throws it away.
   *
   * An already-in-flight PATCH cannot be recalled, so it is AWAITED rather
   * than ignored. The entry is then deleted unconditionally: after a reset
   * the server is the truth, and a retained `ackedPatch` would make the next
   * send treat the discarded edit as the rep's current choice.
   */
  const discardAutosave = useCallback(async (itemId: number) => {
    const entry = autosaveEntriesRef.current.get(itemId);
    if (!entry) return;
    if (entry.timer !== null) {
      clearTimeout(entry.timer);
      entry.timer = null;
    }
    entry.pendingPatch = null;
    // Settle-chained, so it never rejects (see `DebouncedWriteEntry`) — a
    // failed in-flight write is irrelevant here, the reset overwrites it.
    if (entry.inFlight !== null) await entry.inFlight;
    autosaveEntriesRef.current.delete(itemId);
  }, []);

  /**
   * Drains `itemId`'s entry until genuinely idle: cancels the debounce timer,
   * dispatches anything pending, and — round-3 (F1) — also awaits a PATCH
   * that is ALREADY in flight. The Send flow's required first step (U2
   * behavior spec #2): guarantees the rep's final edits are on the server
   * BEFORE the mark-ready PATCH and the send POST that follow it. A no-op
   * (resolves immediately, no network call) when nothing is unsaved or in
   * flight for `itemId`. Rejects with the failing PATCH's error — after that
   * write has settled — and leaves the patch tracked for a later retry; one
   * retry attempt per flush call.
   */
  const flushAutosave = useCallback(
    async (itemId: number) => {
      for (;;) {
        // ── D12 Codex round-3 fix #1 — a flush must not LIE about draining ──
        await awaitItemOps(itemId);
        const entry = autosaveEntriesRef.current.get(itemId);
        if (!entry) return;
        if (entry.timer !== null) {
          clearTimeout(entry.timer);
          entry.timer = null;
        }
        if (entry.inFlight === null) {
          if (entry.pendingPatch === null) return;
          dispatchAutosave(itemId);
        }
        const flight = autosaveEntriesRef.current.get(itemId)?.inFlight;
        if (!flight) return;
        await flight; // settle-chained — never rejects; failure is on lastError
        const post = autosaveEntriesRef.current.get(itemId);
        if (post && post.lastError != null) {
          throw asFlushError(post.lastError);
        }
        // Success — loop: a patch queued mid-flight (or a fresh edit) gets
        // dispatched by the next pass, so the flush only resolves once the
        // item is genuinely drained.
      }
    },
    [dispatchAutosave, awaitItemOps],
  );

  return { patchItem, autosavePatch, dispatchAutosave, flushAutosave, discardAutosave, autosaveEntriesRef };
}
